import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Image, Spinner, Alert } from 'react-bootstrap';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const History = () => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    fetchHistoryData();
    // Trigger animations after component mounts
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${BASE_URL}/public/about.php?action=getAll`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setHistoryData(result.data);
      } else {
        setError(result.message || 'Failed to fetch history data');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Error loading history: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = () => {
    if (!historyData) return 'https://via.placeholder.com/300x150?text=College+Logo';
    
    if (historyData.logo_url) return historyData.logo_url;
    
    if (historyData.logo) {
      return getImageUrl(historyData.logo);
    }
    
    return 'https://via.placeholder.com/300x150?text=College+Logo';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/500x300?text=College+Logo';
    
    if (imagePath.startsWith('http')) return imagePath;
    
    if (imagePath.startsWith('uploads/')) {
      return `${UPLOAD_BASE_URL}/${imagePath}`;
    }
    
    return `${UPLOAD_BASE_URL}/${imagePath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getHistoryParagraphs = () => {
    if (!historyData || !historyData.history) return [];
    return historyData.history.split('\n\n').filter(p => p.trim() !== '');
  };

  if (loading) {
    return (
      <>
        <div style={{ height: '140px' }}></div>
        <Container className="py-5 text-center">
          <div className="loading-wrapper">
            <Spinner animation="border" variant="primary" className="loading-spinner" />
            <p className="mt-3 loading-text">Loading our journey through time...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ height: '140px' }}></div>
        <Container className="py-5">
          <Alert variant="danger" className="animated-alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-2">
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={fetchHistoryData}
              >
                <i className="fas fa-redo me-1"></i>
                Try Again
              </button>
            </div>
          </Alert>
        </Container>
      </>
    );
  }

  const historyParagraphs = getHistoryParagraphs();

  return (
    <>
      {/* Navbar Spacer */}
      <div style={{ height: '140px' }}></div>
      
      {/* Animated Background Elements */}
      <div className="animated-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
        <div className="bg-shape shape-4"></div>
      </div>
      
      <Container className="py-5 position-relative">
        {/* Header with Logo */}
        <Row className="mb-5">
          <Col className="text-center">
            <div className={`logo-container ${isVisible ? 'animate-logo' : ''}`}>
              <div className="logo-wrapper">
                <div className="logo-orbital-ring"></div>
                <div className="logo-glow-effect"></div>
                <Image 
                  src={getLogoUrl()}
                  alt="College Logo" 
                  fluid
                  className="logo-image"
                />
              </div>
              <div className={`header-content ${isVisible ? 'fade-in-up' : ''}`}>
                <h1 className="main-title">
                  <span className="title-text">Our</span>
                  <span className="title-highlight">History</span>
                </h1>
                <p className="subtitle">
                  <span className="subtitle-text">
                    {historyData?.title || 'College History Timeline'}
                  </span>
                </p>
                <div className="timeline-pulse">
                  <div className="pulse-dot"></div>
                  <div className="pulse-ring ring-1"></div>
                  <div className="pulse-ring ring-2"></div>
                  <div className="pulse-ring ring-3"></div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* History Content Card */}
        <Row className="justify-content-center mb-5">
          <Col lg={10} xl={8}>
            <div className={`history-card-wrapper ${isVisible ? 'slide-in' : ''}`}>
              <Card className="animated-card">
                {/* Card Header with Timeline */}
                <div className="card-timeline">
                  <div className="timeline-track">
                    <div className="timeline-marker start">
                      <i className="fas fa-seedling"></i>
                    </div>
                    <div className="timeline-line">
                      <div className="line-progress"></div>
                    </div>
                    <div className="timeline-marker end">
                      <i className="fas fa-university"></i>
                    </div>
                  </div>
                </div>
                
                <Card.Body className="p-4 p-md-5">
                  {historyParagraphs.length > 0 ? (
                    <div className="history-content">
                      {/* Animated Decorative Elements */}
                      <div className="content-decoration">
                        <div className="decoration-circle circle-1"></div>
                        <div className="decoration-circle circle-2"></div>
                        <div className="decoration-line"></div>
                      </div>
                      
                      {historyParagraphs.map((paragraph, index) => (
                        <div 
                          key={index} 
                          className={`history-paragraph ${isVisible ? 'paragraph-enter' : ''}`}
                          style={{ animationDelay: `${index * 0.2}s` }}
                        >
                          {/* Paragraph Marker */}
                          <div className="paragraph-marker">
                            <span className="marker-number">{index + 1}</span>
                            <div className="marker-line"></div>
                          </div>
                          
                          {/* Paragraph Content */}
                          <div className="paragraph-content">
                            {index === 0 ? (
                              <p className="paragraph-text">
                                <span className="drop-cap">{paragraph.charAt(0)}</span>
                                {paragraph.slice(1)}
                              </p>
                            ) : (
                              <p className="paragraph-text">{paragraph}</p>
                            )}
                            
                            {/* Decorative Separator */}
                            {index < historyParagraphs.length - 1 && (
                              <div className="paragraph-separator">
                                <div className="separator-dots">
                                  <span className="dot"></span>
                                  <span className="dot"></span>
                                  <span className="dot"></span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert variant="info" className="animated-alert">
                      <i className="fas fa-info-circle me-2"></i>
                      History content is being updated. Please check back soon.
                    </Alert>
                  )}
                </Card.Body>
                
                {/* Card Footer with Animated Date */}
                <Card.Footer className="history-footer">
                  <div className="footer-content">
                    <div className="updated-info">
                      <div className="date-icon">
                        <i className="fas fa-clock"></i>
                        <div className="icon-pulse"></div>
                      </div>
                      <span className="date-text">
                        Last updated: <span className="date-highlight">{formatDate(historyData?.updated_at)}</span>
                      </span>
                    </div>
                    <div className="scroll-indicator">
                      <div className="scroll-dots">
                        <span className="scroll-dot active"></span>
                        <span className="scroll-dot"></span>
                        <span className="scroll-dot"></span>
                      </div>
                    </div>
                  </div>
                </Card.Footer>
              </Card>
            </div>
          </Col>
        </Row>

        {/* Floating Historical Elements */}
        <div className="floating-elements">
          <div className="floating-element element-1">
            <i className="fas fa-book-open"></i>
          </div>
          <div className="floating-element element-2">
            <i className="fas fa-history"></i>
          </div>
          <div className="floating-element element-3">
            <i className="fas fa-landmark"></i>
          </div>
        </div>
      </Container>

      {/* Advanced CSS Animations and Styles */}
      <style jsx="true">{`
        /* Base Styles */
        .history-content {
          line-height: 1.8;
        }
        
        /* Loading Animation */
        .loading-wrapper {
          position: relative;
          min-height: 300px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
        
        .loading-spinner {
          width: 3rem;
          height: 3rem;
          animation: rotate 1.5s linear infinite;
        }
        
        .loading-text {
          font-size: 1.2rem;
          background: linear-gradient(45deg, #0d6efd, #198754);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: colorShift 3s infinite alternate;
        }
        
        /* Animated Background */
        .animated-background {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          overflow: hidden;
        }
        
        .bg-shape {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(45deg, rgba(13, 110, 253, 0.1), rgba(25, 135, 84, 0.05));
          filter: blur(40px);
        }
        
        .shape-1 {
          width: 300px;
          height: 300px;
          top: 10%;
          left: 5%;
          animation: float 20s infinite alternate ease-in-out;
        }
        
        .shape-2 {
          width: 200px;
          height: 200px;
          top: 60%;
          right: 10%;
          animation: float 25s infinite alternate-reverse ease-in-out;
        }
        
        .shape-3 {
          width: 150px;
          height: 150px;
          bottom: 20%;
          left: 15%;
          animation: float 30s infinite alternate ease-in-out;
        }
        
        .shape-4 {
          width: 250px;
          height: 250px;
          top: 30%;
          right: 5%;
          animation: float 35s infinite alternate-reverse ease-in-out;
        }
        
        /* Logo Animation */
        .logo-container {
          position: relative;
          margin-bottom: 3rem;
        }
        
        .logo-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 2rem;
        }
        
        .logo-orbital-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 200px;
          height: 200px;
          border: 2px dashed rgba(13, 110, 253, 0.3);
          border-radius: 50%;
          animation: spin 20s linear infinite;
        }
        
        .logo-glow-effect {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 180px;
          height: 180px;
          background: radial-gradient(circle, rgba(13, 110, 253, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulse 4s infinite alternate;
        }
        
        .logo-image {
          max-height: 150px;
          width: auto;
          position: relative;
          z-index: 2;
          filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15));
          transition: transform 0.5s ease;
        }
        
        .animate-logo .logo-image {
          animation: bounceIn 1s ease-out;
        }
        
        /* Header Animations */
        .main-title {
          font-size: 4rem;
          font-weight: 800;
          margin-bottom: 1rem;
          position: relative;
          display: inline-block;
        }
        
        .title-text {
          color: #333;
          position: relative;
          animation: textReveal 1s ease-out;
        }
        
        .title-highlight {
          background: linear-gradient(45deg, #0d6efd, #198754);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s infinite alternate;
          margin-left: 10px;
        }
        
        .subtitle {
          font-size: 1.2rem;
          color: #6c757d;
          margin-bottom: 2rem;
        }
        
        .subtitle-text {
          display: inline-block;
          animation: slideIn 0.8s ease-out 0.5s both;
        }
        
        /* Timeline Pulse Animation */
        .timeline-pulse {
          position: relative;
          display: flex;
          justify-content: center;
          margin-top: 2rem;
        }
        
        .pulse-dot {
          width: 12px;
          height: 12px;
          background: #0d6efd;
          border-radius: 50%;
          position: relative;
          z-index: 2;
          animation: heartbeat 1.5s infinite;
        }
        
        .pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          border: 2px solid rgba(13, 110, 253, 0.3);
          border-radius: 50%;
          animation: ripple 2s infinite;
        }
        
        .ring-1 { width: 40px; height: 40px; animation-delay: 0s; }
        .ring-2 { width: 60px; height: 60px; animation-delay: 0.5s; }
        .ring-3 { width: 80px; height: 80px; animation-delay: 1s; }
        
        /* Card Animations */
        .history-card-wrapper {
          perspective: 1000px;
        }
        
        .animated-card {
          border: none;
          border-radius: 20px;
          background: linear-gradient(135deg, #ffffff, #f8f9fa);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          transform-style: preserve-3d;
          transition: transform 0.6s ease;
        }
        
        .animated-card:hover {
          transform: translateY(-10px) rotateX(2deg);
        }
        
        .slide-in {
          animation: slideInFromRight 0.8s ease-out;
        }
        
        /* Timeline Animation */
        .card-timeline {
          padding: 2rem 0;
          position: relative;
        }
        
        .timeline-track {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        
        .timeline-marker {
          width: 50px;
          height: 50px;
          background: linear-gradient(45deg, #0d6efd, #198754);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          z-index: 2;
          animation: bounce 2s infinite;
        }
        
        .timeline-line {
          flex: 1;
          height: 4px;
          background: linear-gradient(90deg, 
            rgba(13, 110, 253, 0.2) 0%, 
            rgba(13, 110, 253, 0.6) 50%, 
            rgba(13, 110, 253, 0.2) 100%);
          margin: 0 20px;
          position: relative;
          overflow: hidden;
        }
        
        .line-progress {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 0;
          background: linear-gradient(90deg, #0d6efd, #198754);
          animation: timelineProgress 3s ease-in-out infinite alternate;
        }
        
        /* Paragraph Animations */
        .history-paragraph {
          margin-bottom: 2.5rem;
          display: flex;
          opacity: 0;
        }
        
        .paragraph-enter {
          animation: fadeInLeft 0.6s ease forwards;
        }
        
        .paragraph-marker {
          flex-shrink: 0;
          margin-right: 2rem;
          position: relative;
        }
        
        .marker-number {
          display: block;
          width: 40px;
          height: 40px;
          background: linear-gradient(45deg, #0d6efd, #198754);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 1.2rem;
          animation: countUp 0.5s ease-out;
        }
        
        .marker-line {
          position: absolute;
          top: 40px;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: 100%;
          background: linear-gradient(180deg, #0d6efd, transparent);
        }
        
        .paragraph-content {
          flex: 1;
        }
        
        .drop-cap {
          font-size: 5rem;
          font-weight: 800;
          float: left;
          line-height: 0.8;
          margin-right: 15px;
          margin-top: 8px;
          background: linear-gradient(45deg, #0d6efd, #198754);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s infinite alternate;
        }
        
        .paragraph-text {
          font-size: 1.1rem;
          line-height: 1.9;
          color: #333;
          text-align: justify;
        }
        
        .paragraph-separator {
          text-align: center;
          margin: 2rem 0;
        }
        
        .separator-dots {
          display: flex;
          justify-content: center;
          gap: 10px;
        }
        
        .dot {
          width: 8px;
          height: 8px;
          background: #0d6efd;
          border-radius: 50%;
          animation: dotBounce 1.5s infinite;
        }
        
        .dot:nth-child(2) { animation-delay: 0.2s; }
        .dot:nth-child(3) { animation-delay: 0.4s; }
        
        /* Footer Animations */
        .history-footer {
          background: linear-gradient(135deg, #f8f9fa, #ffffff);
          border-top: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        .footer-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .updated-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .date-icon {
          position: relative;
          color: #0d6efd;
          font-size: 1.2rem;
        }
        
        .icon-pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 30px;
          height: 30px;
          background: rgba(13, 110, 253, 0.2);
          border-radius: 50%;
          animation: iconPulse 2s infinite;
        }
        
        .date-text {
          color: #6c757d;
        }
        
        .date-highlight {
          color: #0d6efd;
          font-weight: 600;
          animation: highlightPulse 2s infinite;
        }
        
        .scroll-indicator {
          display: flex;
          gap: 5px;
        }
        
        .scroll-dot {
          width: 8px;
          height: 8px;
          background: #dee2e6;
          border-radius: 50%;
          animation: scrollDot 3s infinite;
        }
        
        .scroll-dot.active {
          background: #0d6efd;
        }
        
        .scroll-dot:nth-child(2) { animation-delay: 0.5s; }
        .scroll-dot:nth-child(3) { animation-delay: 1s; }
        
        /* Floating Elements */
        .floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          pointer-events: none;
          z-index: -1;
        }
        
        .floating-element {
          position: absolute;
          color: rgba(13, 110, 253, 0.1);
          font-size: 2rem;
          animation: floatElement 20s infinite linear;
        }
        
        .element-1 {
          top: 20%;
          left: 10%;
          animation-delay: 0s;
        }
        
        .element-2 {
          top: 60%;
          right: 15%;
          animation-delay: 5s;
        }
        
        .element-3 {
          bottom: 30%;
          left: 20%;
          animation-delay: 10s;
        }
        
        /* Animation Keyframes */
        @keyframes spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { opacity: 0.3; transform: translate(-50%, -50%) scale(0.8); }
          100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.2); }
        }
        
        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes textReveal {
          from { clip-path: inset(0 100% 0 0); }
          to { clip-path: inset(0 0 0 0); }
        }
        
        @keyframes gradientShift {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
        
        @keyframes slideIn {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.3); }
        }
        
        @keyframes ripple {
          0% { width: 0; height: 0; opacity: 1; }
          100% { width: 100px; height: 100px; opacity: 0; }
        }
        
        @keyframes slideInFromRight {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes fadeInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes timelineProgress {
          0% { width: 0%; left: 0; }
          100% { width: 100%; left: 0; }
        }
        
        @keyframes countUp {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        
        @keyframes dotBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes iconPulse {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.2); }
        }
        
        @keyframes highlightPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes scrollDot {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        @keyframes floatElement {
          0% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-20px) rotate(90deg); }
          50% { transform: translateY(0) rotate(180deg); }
          75% { transform: translateY(20px) rotate(270deg); }
          100% { transform: translateY(0) rotate(360deg); }
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes colorShift {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          div[style*="height"] {
            height: 90px !important;
          }
          
          .main-title {
            font-size: 2.5rem;
          }
          
          .drop-cap {
            font-size: 3.5rem;
          }
          
          .paragraph-text {
            font-size: 1rem;
          }
          
          .history-paragraph {
            flex-direction: column;
          }
          
          .paragraph-marker {
            margin-right: 0;
            margin-bottom: 1rem;
          }
          
          .marker-line {
            display: none;
          }
          
          .animated-card {
            border-radius: 15px;
          }
          
          .logo-image {
            max-height: 120px;
          }
          
          .logo-orbital-ring {
            width: 160px;
            height: 160px;
          }
        }
        
        @media (min-width: 992px) {
          div[style*="height"] {
            height: 140px !important;
          }
        }
        
        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #0d6efd, #198754);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #0b5ed7, #157347);
        }
      `}</style>
    </>
  );
};

export default History;